import React, { useState, useEffect, useCallback } from "react";
import {
  Settings2, RefreshCw, Plug, CheckCircle2, XCircle, Save, KeyRound, Scale,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import SecopTokenPoolSection from "../components/SecopTokenPoolSection";
import SecopDatasetsSection from "../components/SecopDatasetsSection";

const GROUPS = [
  { id: "api", title: "Acceso API datos.gov.co", icon: <KeyRound size={14} /> },
  { id: "limits", title: "Caché y límites", icon: <Settings2 size={14} /> },
  { id: "weights", title: "Pesos del re-ranking (learning loop)", icon: <Scale size={14} /> },
];

const SOURCE_BADGE = {
  db: "bg-indigo-50 border-indigo-200 text-indigo-700",
  env: "bg-amber-50 border-amber-200 text-amber-700",
  default: "bg-gray-50 border-gray-200 text-gray-500",
};

export default function AdminSecopConfigPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [poolStats, setPoolStats] = useState({ active: 0 });
  const [datasetsCount, setDatasetsCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cfg }, { data: tok }, { data: ds }] = await Promise.all([
        api.get("/admin/secop/config"),
        api.get("/admin/secop/tokens").catch(() => ({ data: { totals: { active: 0 } } })),
        api.get("/admin/secop/datasets").catch(() => ({ data: { totals: { count: 0 } } })),
      ]);
      setItems(cfg.items || []);
      setEdits({});
      setPoolStats(tok.totals || { active: 0 });
      setDatasetsCount(ds.totals?.count || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar config SECOP");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const dirtyKeys = Object.keys(edits);

  const saveAll = useCallback(async () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    let ok = 0;
    for (const key of dirtyKeys) {
      try {
        await api.post("/admin/secop/config", { key, value: String(edits[key]) });
        ok += 1;
      } catch (e) {
        toast.error(`${key}: ${formatApiError(e?.response?.data?.detail) || "error al guardar"}`);
      }
    }
    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok} configuración(es) guardada(s)`);
      refresh();
    }
  }, [dirtyKeys, edits, refresh]);

  const testConnection = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/secop/config/test");
      setTestResult(data);
      const allOk = data.overall_ok;
      const n = data.tokens_tested || 0;
      if (allOk) toast.success(`Conexión SECOP OK · ${n} token(s) probado(s)`);
      else toast.error(`Algún token falló — revisa el detalle (${n} probado(s))`);
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail);
      toast.error(detail || "Error al probar conexión");
    } finally {
      setTesting(false);
    }
  }, []);

  const noTokens = poolStats.active === 0;

  return (
    <div data-testid="admin-secop-config-page" className="space-y-6">
      <header className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div>
          <h1 className="font-serif text-2xl text-gray-900">Configuración SECOP</h1>
          <p className="text-xs text-gray-500 mt-1">
            Accesos a la API de datos.gov.co, caché, límites y pesos del re-ranking.
            Prioridad: DB → variable de entorno → default.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={testConnection}
            disabled={testing || noTokens}
            className="rounded-sm h-9 text-xs"
            data-testid="secop-config-test-btn"
            title={noTokens ? "Agrega al menos un token al pool para probar la conexión" : "Probar todos los tokens"}
          >
            <Plug size={14} className={`mr-1.5 ${testing ? "animate-pulse" : ""}`} />
            {testing ? "Probando…" : noTokens ? "Probar conexión (sin tokens)" : "Probar todos los tokens"}
          </Button>
          <Button
            onClick={saveAll}
            disabled={saving || dirtyKeys.length === 0}
            className="rounded-sm h-9 text-xs"
            data-testid="secop-config-save-btn"
          >
            <Save size={14} className="mr-1.5" />
            {saving ? "Guardando…" : `Guardar cambios${dirtyKeys.length ? ` (${dirtyKeys.length})` : ""}`}
          </Button>
          <Button variant="outline" onClick={refresh} disabled={loading} className="rounded-sm h-9 px-3" data-testid="secop-config-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </header>

      {/* Resultado test conexión */}
      {testResult && (
        <div className="border border-gray-200 bg-white p-4" data-testid="secop-config-test-result">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">
              Resultado de la prueba · {testResult.tokens_tested} token(s)
            </div>
            <div className={`text-[11px] font-medium px-2 py-0.5 border rounded-sm ${
              testResult.overall_ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"
            }`}>
              {testResult.overall_ok ? "Todos OK" : "Hay fallos"}
            </div>
          </div>
          <div className="space-y-3">
            {(testResult.per_token || []).map((t, idx) => (
              <div
                key={t.token_id || `legacy-${idx}`}
                className={`border ${t.ok ? "border-emerald-200" : "border-rose-200"} bg-white`}
                data-testid={`secop-test-token-${idx}`}
              >
                <div className={`px-3 py-2 flex items-center gap-2 ${t.ok ? "bg-emerald-50/50" : "bg-rose-50/50"}`}>
                  {t.ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-rose-600" />}
                  <span className="text-sm font-medium">{t.label}</span>
                  {!t.token_id && <span className="text-[10px] uppercase text-amber-700 ml-1">legacy</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-x divide-gray-100 text-xs">
                  {["procesos", "contratos"].map((ds) => {
                    const r = t.results?.[ds];
                    if (!r) return null;
                    return (
                      <div key={ds} className="p-2.5" data-testid={`secop-test-token-${idx}-${ds}`}>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          {r.ok ? <CheckCircle2 size={11} className="text-emerald-600" /> : <XCircle size={11} className="text-rose-600" />}
                          <span className="capitalize font-medium">{ds}</span>
                          <span className="font-mono text-[10px] text-gray-400">{r.dataset_id}</span>
                        </div>
                        <div className="mt-0.5 text-gray-600 text-[11px]">
                          {r.ok
                            ? `OK · ${r.latency_ms}ms · ${r.items_returned} item(s)`
                            : `Falló: ${(r.error || "error desconocido").slice(0, 120)}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Base: {testResult.config_used?.base_url} · Token:{" "}
            {testResult.config_used?.token_source === "pool" ? (
              <span className="text-indigo-700">pool ({testResult.config_used?.token_label})</span>
            ) : testResult.config_used?.token_source === "legacy_config" ? (
              <span className="text-amber-700">config legacy</span>
            ) : (
              <span className="text-gray-500">sin token</span>
            )}{" "}
            · Timeout: {testResult.config_used?.timeout_s}s
          </div>
        </div>
      )}

      {/* Pool de tokens Socrata (rotación por menor consumo) */}
      <SecopTokenPoolSection onPoolChanged={refresh} />

      {/* Datasets SECOP / datos.gov.co (registry para análisis completo) */}
      <SecopDatasetsSection />

      {/* Grupos de configuración */}
      {(() => {
        const apiGroupRedundant = poolStats.active >= 2 && datasetsCount >= 7;
        return (
          <>
            {apiGroupRedundant && (
              <div
                className="border border-emerald-200 bg-emerald-50/40 px-4 py-2 text-[11px] text-emerald-800 flex items-center gap-2"
                data-testid="secop-api-group-hidden-notice"
              >
                <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                <span>
                  Configuración base gestionada por el pool de tokens ({poolStats.active} activos) y el
                  registry de datasets ({datasetsCount} registrados). Los inputs avanzados de &ldquo;Acceso API&rdquo;
                  están ocultos para reducir ruido — usa el pool para tokens y el registry para datasets.
                </span>
              </div>
            )}
            {GROUPS.map((g) => {
              if (g.id === "api" && apiGroupRedundant) return null;
              const groupItems = items.filter((it) => it.group === g.id);
              if (groupItems.length === 0 && !loading) return null;
              return (
                <div key={g.id} className="border border-gray-200 bg-white" data-testid={`secop-config-group-${g.id}`}>
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                    {g.icon}
                    <h2 className="font-serif text-base">{g.title}</h2>
                  </div>
                  {g.id === "weights" && <WeightsHelpBanner />}
                  <div className="divide-y divide-gray-100">
              {groupItems.map((it) => {
                const edited = edits[it.key] !== undefined;
                return (
                  <div key={it.key} className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-2 items-center" data-testid={`secop-config-field-${it.key}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-800">{it.label}</span>
                        <span className={`px-1.5 py-0.5 border rounded-sm text-[9px] uppercase tracking-wide ${SOURCE_BADGE[it.source] || SOURCE_BADGE.default}`}>
                          {it.source}
                        </span>
                        {edited && <span className="text-[10px] text-amber-600">● sin guardar</span>}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">{it.description}</p>
                      {it.key === "secop_app_token" && (
                        <p className="text-[10px] text-indigo-700 mt-0.5 italic">
                          Si registras tokens en el pool de arriba, el pool toma precedencia sobre este valor legacy.
                        </p>
                      )}
                      <p className="text-[10px] text-gray-400 font-mono mt-0.5">{it.key} · env: {it.env_var}</p>
                    </div>
                    <div>
                      <Input
                        type={it.secret ? "password" : "text"}
                        placeholder={it.secret ? (it.is_set ? it.value : "No configurado — pegar token") : ""}
                        value={edited ? edits[it.key] : (it.secret ? "" : String(it.value ?? ""))}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [it.key]: e.target.value }))}
                        className="h-9 text-xs rounded-sm font-mono"
                        data-testid={`secop-config-input-${it.key}`}
                      />
                      {it.secret && it.is_set && !edited && (
                        <p className="text-[10px] text-gray-400 mt-1">Valor actual: {it.value} — escribir uno nuevo lo reemplaza; guardar vacío lo borra.</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
          </>
        );
      })()}

      {loading && items.length === 0 && (
        <div className="p-8 text-center text-sm text-gray-500">Cargando configuración…</div>
      )}
    </div>
  );
}

function WeightsHelpBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-amber-100 bg-amber-50/40" data-testid="secop-weights-help-banner">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2 text-left flex items-center justify-between text-xs text-gray-700 hover:bg-amber-50"
        data-testid="secop-weights-help-toggle"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <span className="font-medium">¿Cómo funciona el re-ranking y qué influye cada peso?</span>
        </span>
        <span className="text-[10px] text-amber-700 uppercase tracking-widest">Guía</span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 text-[11px] text-gray-700 space-y-2 leading-relaxed">
          <p>
            Cuando una búsqueda SECOP devuelve candidatos, los re-rankamos sumando
            <strong> 6 señales ponderadas</strong> antes de mostrarlos como comparables. La fórmula es aproximadamente:
          </p>
          <pre className="bg-white border border-gray-200 px-2 py-1.5 font-mono text-[10.5px] overflow-x-auto rounded-sm">
score(c) = W_keywords · sim_texto(intake, c.objeto)
         + W_entidad  · match_tipo_entidad
         + W_region   · match_departamento
         + W_valor    · esta_en_banda(±valor_band_pct)
         + W_modalidad· match_modalidad_normalizada
         + W_feedback · boost_feedback_historico(c)
          </pre>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>sim_texto</strong>: Jaccard sobre tokens limpios (sin stopwords ES).</li>
            <li><strong>match_*</strong>: 1 si coincide la normalización (ej. &ldquo;alcaldía&rdquo;, &ldquo;minima_cuantia&rdquo;), 0 si no.</li>
            <li><strong>esta_en_banda</strong>: 1 si el valor del candidato está dentro de ±X% del valor del intake (X = banda de valor).</li>
            <li><strong>boost_feedback</strong>: +1 por cada feedback &ldquo;útil&rdquo; del learning loop, −1 por cada &ldquo;no útil&rdquo;, normalizado.</li>
          </ul>
          <p className="text-gray-600">
            <strong>Pesos por defecto</strong>: keywords <code>1.0</code>, entidad <code>0.6</code>, región <code>0.4</code>,
            valor <code>0.8</code>, modalidad <code>0.5</code>, feedback <code>0.7</code>, banda valor <code>0.30</code> (±30%).
            Sube un peso para que esa señal domine; bájalo a <code>0</code> para deshabilitarla.
          </p>
          <p className="text-gray-600 italic">
            Recomendación: cambia un peso a la vez, mide el % de comparables marcados &ldquo;útil&rdquo; en 2-3 semanas, y ajusta.
          </p>
        </div>
      )}
    </div>
  );
}

